import { Header, Link } from '@cloudscape-design/components';
import { Spinner } from '@cloudscape-design/components';
import { ProgressBar } from '@cloudscape-design/components';
import { SpaceBetween } from '@cloudscape-design/components';
import { Container } from '@cloudscape-design/components';
import { Grid } from '@cloudscape-design/components';
import { StarsIcon } from './StarsIcon';
import { PlaceBasic, PlaceReport, Task, TASK_NAME_MAP } from '../common/types';
import { useState } from 'react';
import { useEffect } from 'react';
import { TASK_NAME_INFO_MAP } from '../common/types';

export type CardTaskProgressViewProps = {
  place?: PlaceBasic | null;
  currentTask?: Task;
  status: 'in-progress' | 'success' | 'error';
};

export function CardTaskProgressView({ place, currentTask, status }: CardTaskProgressViewProps) {
  const currentName = currentTask ? TASK_NAME_MAP[currentTask.name] : 'Collecting details...';
  const currentInfo = currentTask
    ? TASK_NAME_INFO_MAP[currentTask.name]
    : 'Collecting details from Google Maps...';
  const totalReviews = place?.total_review_count ?? 100;
  // report needs 180 days of reviews, so we're going to try to estimate how long it will take to collect them and make
  // it proportional to the total reviews. The task doesn't update live, so we will just have a timer refreshing every second
  // to show the progress.
  const [progress, setProgress] = useState<number>(0);

  // here we will start by checking how this works for current places.
  // first measurement: 10 seconds for 3500 review place
  // update frequency is every second
  const updateInterval = 1000;
  // 100 units to update for 10 seconds
  const updateUnitsPerSecond = 100.0 / 10;
  // now normalize to total reviews
  const reviewFactor = totalReviews / 3500;
  const placeFactor = (updateUnitsPerSecond * reviewFactor) / 5;
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        const newProgress = p + 1.0 * placeFactor;
        if (newProgress >= 99) {
          // don't show 100% until the task is actually done
          return 99;
        }
        return newProgress;
      });
    }, updateInterval);
    return () => clearInterval(interval);
  }, []);

  return (
    <Grid
      gridDefinition={[
        {
          colspan: {
            default: 12,
          },
        },
      ]}
    >
      <Container
        header={
          <Header
            variant="h1"
            description={
              place
                ? `Working on your Reputation Scorecard for ${place.name}...`
                : `Working on your Reputation Scorecard...`
            }
          >
            Creating your Reputation Scorecard
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="s">
          <Container>
            {place ? (
              <Header
                variant="h2"
                description={
                  <SpaceBetween direction="vertical" size="xxxs">
                    {place?.address}
                    <SpaceBetween direction="horizontal" size="xs">
                      {place.rating?.toFixed(1) ?? '?.?'}
                      <StarsIcon stars={place.rating} />
                      <Link href={place.cid_url} target="_blank">
                        {place.total_review_count?.toLocaleString() ?? '?'} Google reviews
                      </Link>
                    </SpaceBetween>
                  </SpaceBetween>
                }
              >
                {place.name}
              </Header>
            ) : (
              <Spinner size="large" />
            )}
          </Container>
          <ProgressBar
            label={currentName}
            // in-progress, success, or error
            status={status}
            // 0 to 100 as a percentage
            value={status === 'in-progress' ? progress : 100}
            additionalInfo={currentInfo}
          />
        </SpaceBetween>
      </Container>
    </Grid>
  );
}
